import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import InfiniteScroll from "src/components/infiniteScroll";
import GroupThumbnail from "src/pages/coaching/manage/courses/edit/components/groupThumbnail";
import { Checkbox } from "src/components/input";
import Actions from "src/components/actionbar";
import { LearnerPlaceholder } from "src/pages/coaching/manage/courses/edit/components/learners";
import { searchLearners } from "src/redux/actions/search";
import Text from "src/components/text";

import _ from "lodash";
import cx from "classnames";
import styles from "./userGroup.module.scss";
import { ReportState } from "../..";
import { UserGroup } from "src/types/users";

interface UserGroupsProps {
  reportState: ReportState;
  // eslint-disable-next-line no-unused-vars
  onReportStateChange: (state: any) => void;
}

const UserGroups: React.FC<UserGroupsProps> = ({
  reportState,
  onReportStateChange,
}) => {
  const dispatch = useDispatch();

  const { loading, userGroups, currentPage, nextPage, hasMoreItems } =
    useSelector((state: any) => {
      const userGroupState = _.get(state, "search.learners");
      const loading = _.get(userGroupState, "loading");
      const pagination = _.get(userGroupState, "pagination");
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");
      const currentPage = _.get(pagination, "currentPage", 0);

      const userGroups = _.get(userGroupState, "items") || [];

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        userGroups: userGroups,
        currentPage: currentPage,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    }, _.isEqual);

  const [query, setQuery] = useState<string | null>("");

  const fetchLearnersForPage = (query, page) => {
    const request = {
      query: query || "",
      page: page,
      limit: 20,
    };

    dispatch(searchLearners(request));
  };

  const onSelect = (group: UserGroup) => {
    let items = [...reportState.userGroups];

    const selected = _.find(items, (item) => item.id === group.id);

    if (selected) {
      items = items.filter((item) => item.id !== group.id);
    } else {
      items = [...items, group];
    }

    onReportStateChange({
      ...reportState,
      userGroups: items,
    });
  };

  const handleQueryChange = (query: any) => {
    setQuery(query || "");
  };

  const handleLoadMoreUserGroups = () => {
    if (nextPage) {
      fetchLearnersForPage(query, nextPage);
    }
  };

  useEffect(() => {
    fetchLearnersForPage(query, 0);
  }, [query]);

  return (
    <div className={styles.container}>
      <div className={styles.inputContainer}>
        <Actions.Search query={query} onSearch={handleQueryChange} />
      </div>
      <InfiniteScroll
        className={styles.learnersList}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreUserGroups}
        loader={() => {
          return _.times(
            _.size(userGroups) === 0 || currentPage == 0 ? 4 : 1,
            (index) => <LearnerPlaceholder key={index} />,
          );
        }}
      >
        {_.size(userGroups) === 0 && !loading && (
          <span className={styles.noRecordsFound}>No results found.</span>
        )}

        {_.size(userGroups) > 0 && (
          <UserGroupsList
            userGroups={userGroups}
            selectedGroups={reportState.userGroups}
            onSelect={onSelect}
          />
        )}
      </InfiniteScroll>
    </div>
  );
};

interface UserGroupsListProps {
  userGroups: any[];
  selectedGroups: any[];
  // eslint-disable-next-line no-unused-vars
  onSelect: (group: UserGroup) => void;
}

const UserGroupsList: React.FC<UserGroupsListProps> = ({
  userGroups,
  selectedGroups,
  onSelect,
}) => {
  return (
    <>
      {userGroups.map((group) => {
        const selected = _.find(selectedGroups, (item) => item.id === group.id);

        if (group.entityType === "user_group") {
          return (
            <div key={group.id} className={styles.learner}>
              <div className={styles.details}>
                <div className={styles.metadata}>
                  <div className={styles.icon}>
                    <GroupThumbnail icons={group.icons} />
                  </div>
                  <div className={styles.detail}>
                    <Text className={styles.name} lines={1} text={group.name} />
                    <span className={styles.count}>
                      {group.usersCount > 0
                        ? group.usersCount > 1
                          ? `${group.usersCount} users`
                          : `${group.usersCount} user`
                        : "No users"}
                    </span>
                  </div>
                </div>
                <Checkbox checked={selected} onChange={() => onSelect(group)} />
              </div>
            </div>
          );
        }
      })}
    </>
  );
};

export default UserGroups;
